import {FC, useState, useEffect} from 'react'
import Swal from 'sweetalert2'
import {POST} from '../../../services/api'
import {i18n} from '../../../translate/i18n'
import {Input} from '../../Input/Input'
import {Button} from '../../Button/Button'

interface InventoryItem {
  id?: string
  sku: string
  name: string
  code: string
  specification: string
  inventory: number
  minimumStock: number
}

interface InventoryLoteModalProps {
  isOpen: boolean
  variations: any[]
  onClose: () => void
  onSuccess: () => void
}

export const InventoryLoteModal: FC<InventoryLoteModalProps> = ({
  isOpen,
  variations,
  onClose,
  onSuccess,
}) => {
  const [items, setItems] = useState<InventoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState<string>('')

  useEffect(() => {
    if (isOpen && variations.length > 0) {
      const initialItems = variations.map((variation) => ({
        id: String(variation.id),
        sku: variation.sku,
        name: variation.name,
        code: variation.code,
        specification: variation.specification,
        inventory: variation.inventory || 0,
        minimumStock: 0,
      }))
      setItems(initialItems)
    }
  }, [isOpen, variations])

  const handleInventoryChange = (index: number, value: number) => {
    const newItems = [...items]
    newItems[index].inventory = value
    setItems(newItems)
  }

  const handleMinimumStockChange = (index: number, value: number) => {
    const newItems = [...items]
    newItems[index].minimumStock = value
    setItems(newItems)
  }

  const handleSelectItem = (sku: string) => {
    const newSelected = new Set(selectedSkus)
    if (newSelected.has(sku)) {
      newSelected.delete(sku)
    } else {
      newSelected.add(sku)
    }
    setSelectedSkus(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedSkus.size === filteredItems.length) {
      setSelectedSkus(new Set())
    } else {
      const allSkus = new Set(filteredItems.map((item) => item.sku))
      setSelectedSkus(allSkus)
    }
  }

  const filteredItems = items.filter((item) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      item.sku.toLowerCase().includes(searchLower) ||
      item.name.toLowerCase().includes(searchLower) ||
      item.code.toLowerCase().includes(searchLower)
    )
  })

  const handleUpdateInventory = async () => {
    try {
      if (selectedSkus.size === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Nenhum item selecionado',
          text: 'Selecione pelo menos um item para atualizar!',
        })
        return
      }

      const itemsToUpdate = items
        .filter((item) => selectedSkus.has(item.sku))
        .map(({id, name, code, specification, ...rest}) => rest)

      setLoading(true)

      const response = await POST('/variations/inventory-lote', itemsToUpdate)

      setLoading(false)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Estoque atualizado em lote com sucesso!',
        }).then(() => {
          setSelectedSkus(new Set())
          onSuccess()
          onClose()
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response.message || 'Erro ao atualizar estoque!',
        })
      }
    } catch (error: any) {
      setLoading(false)
      console.error('Erro ao atualizar inventário:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error?.message || 'Erro ao atualizar estoque!',
      })
    }
  }

  if (!isOpen) return null

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}}>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Atualizar Estoque em Lote</h5>
            <button
              type='button'
              className='btn-close'
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>
          <div className='modal-body'>
            <div className='mb-3'>
              <input
                type='text'
                className='form-control mb-3'
                placeholder='Pesquisar por SKU, Nome ou Código de Referência...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className='mb-3'>
              <div className='form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='selectAll'
                  checked={selectedSkus.size === filteredItems.length && filteredItems.length > 0}
                  onChange={handleSelectAll}
                  disabled={loading}
                />
                <label className='form-check-label fw-bold' htmlFor='selectAll'>
                  Selecionar todos ({filteredItems.length} de {items.length} itens)
                </label>
              </div>
            </div>

            <div className='table-responsive' style={{maxHeight: '400px', overflow: 'auto'}}>
              <table className='table table-hover table-striped'>
                <thead className='table-light sticky-top'>
                  <tr>
                    <th style={{width: '50px'}}>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={selectedSkus.size === filteredItems.length && filteredItems.length > 0}
                        onChange={handleSelectAll}
                        disabled={loading}
                      />
                    </th>
                    <th>SKU</th>
                    <th>Nome do Produto</th>
                    <th>Código de Referência</th>
                    <th>Especificação</th>
                    <th>Estoque Atual</th>
                    <th>Novo Estoque</th>
                    <th>Estoque Mínimo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className='text-center py-4'>
                        <small className='text-muted'>
                          Nenhum item encontrado para "{searchTerm}"
                        </small>
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const originalIndex = items.findIndex((i) => i.sku === item.sku)
                      return (
                    <tr key={item.sku} className={selectedSkus.has(item.sku) ? 'table-active' : ''}>
                      <td>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={selectedSkus.has(item.sku)}
                          onChange={() => handleSelectItem(item.sku)}
                          disabled={loading}
                        />
                      </td>
                      <td className='fw-bold'>{item.sku}</td>
                      <td>
                        <small>{item.name}</small>
                      </td>
                      <td>
                        <small className='text-muted'>{item.code}</small>
                      </td>
                      <td>
                        <small 
                          dangerouslySetInnerHTML={{__html: item.specification}} 
                          style={{display: 'block', maxHeight: '80px', overflow: 'auto'}}
                        />
                      </td>
                      <td>{variations[originalIndex]?.inventory || 0}</td>
                      <td>
                        <input
                          type='number'
                          className='form-control form-control-sm'
                          value={item.inventory}
                          onChange={(e) => handleInventoryChange(originalIndex, Number(e.target.value))}
                          disabled={loading}
                          min='0'
                        />
                      </td>
                      <td>
                        <input
                          type='number'
                          className='form-control form-control-sm'
                          value={item.minimumStock}
                          onChange={(e) => handleMinimumStockChange(originalIndex, Number(e.target.value))}
                          disabled={loading}
                          min='0'
                        />
                      </td>
                    </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className='alert alert-info mt-3'>
              <i className='bi bi-info-circle me-2'></i>
              <strong>Dica:</strong> Selecione os itens que deseja atualizar e preencha os novos
              valores de estoque.
            </div>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light'
              onClick={onClose}
              disabled={loading}
            >
              {i18n.t('buttons.cancel')}
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleUpdateInventory}
              disabled={loading || selectedSkus.size === 0}
            >
              {loading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                  Atualizando...
                </>
              ) : (
                'Atualizar Estoque'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
